// src/components/Donation/DonationDrawer.js

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  Text,
  Box,
  Flex,
  Spinner,
  IconButton,
  VStack,
  Heading,
  Divider,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FaHeart } from 'react-icons/fa';
import PaymentForm from '../PaymentForm';
import { createDonationIntent, confirmDonation } from '../donations';
import { useAuth } from '../../contexts/AuthContext';

// Inizializza Stripe.js
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC, {
  locale: 'it',
});

const DonationDrawer = ({ isOpen, onClose, goal, amount, donationItem }) => {
  const { userData } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [clientSecret, setClientSecret] = useState('');
  const [localDonationId, setLocalDonationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasConfirmed, setHasConfirmed] = useState(false);

  // reset guard and prior state each time drawer opens
  useEffect(() => {
    if (isOpen) {
      setClientSecret('');
      setLocalDonationId('');
      setError('');
      setHasConfirmed(false);
      // create intent
      (async () => {
        setLoading(true);
        try {
          const { data } = await createDonationIntent({
            userId: userData.id,
            stripeCustomerId: userData.stripeCustomerId,
            amount: Math.round(amount * 100),
            currency: 'eur',
            donationGoalId: goal?.documentId || null, // null se non c'è obiettivo
            metadata: goal
              ? { goalName: goal.title }
              : donationItem
                ? { donationItem }
                : {}, // metadata con goal o item F&B
            type: 'one-time', // tipo donazione singola
          });
          setClientSecret(data.clientSecret);
          setLocalDonationId(data.localDonationId);
        } catch (err) {
          console.error('Errore creazione donation intent', err);
          setError('Impossibile iniziare la donazione. Riprova più tardi.');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [isOpen, goal, amount, donationItem, userData]);

  // Gestisce il successo del pagamento, una sola volta
  const handleSuccess = async ({ paymentIntentId }) => {
    if (hasConfirmed) return; // guard: blocca il secondo invio
    setHasConfirmed(true);

    try {
      const response = await confirmDonation({
        localDonationId,
        paymentIntentId,
      });

      // Gestisce il caso in cui la donazione è già stata confermata via webhook
      if (response.data?.alreadyConfirmed) {
        toast({
          title: 'Donazione già confermata!',
          description: 'La tua donazione è stata processata automaticamente.',
          status: 'success',
          isClosable: true,
        });
      } else {
        toast({
          title: 'Grazie per la tua donazione!',
          status: 'success',
          isClosable: true,
        });
      }

      // Chiudi il drawer e redirect a /account/subscriptions
      onClose();

      // Redirect dopo un breve delay per permettere al toast di essere visibile
      setTimeout(() => {
        navigate('/account/subscriptions');
      }, 1500);
    } catch (err) {
      console.error('Errore conferma donazione', err);
      toast({
        title: 'Errore conferma donazione',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const drawerBg = useColorModeValue('white', 'gray.800');

  // Determina se è una donazione libera o con obiettivo
  const isFreeDonation = !goal;
  const drawerTitle = isFreeDonation ? 'Dona a CIL' : `Dona a "${goal.title}"`;

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="md"
      scrollBehavior="inside"
    >
      <DrawerOverlay bg="blackAlpha.600" />
      <DrawerContent bg={drawerBg}>
        <DrawerHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Heading size="md">{drawerTitle}</Heading>
          <IconButton
            icon={<CloseIcon />}
            aria-label="Chiudi"
            variant="ghost"
            onClick={onClose}
          />
        </DrawerHeader>
        <Divider />
        <DrawerBody p={6}>
          <VStack spacing={4} align="stretch">
            {!isFreeDonation && (
              <>
                <Box>
                  <Text fontSize="sm" color="gray.600">
                    Obiettivo:
                  </Text>
                  <Text fontWeight="bold">{goal.title}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600">
                    Descrizione:
                  </Text>
                  <Text>{goal.description}</Text>
                </Box>
              </>
            )}

            {isFreeDonation && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Tipo donazione:
                </Text>
                <Text fontWeight="bold">Donazione libera</Text>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Il tuo contributo sostiene le attività generali di CIL
                </Text>
              </Box>
            )}

            {/* Testo esplicativo per la donazione */}
            <Box
              bg="blue.50"
              p={4}
              borderRadius="md"
              border="1px solid"
              borderColor="blue.200"
            >
              <Flex align="center" mb={3}>
                <FaHeart color="#e95995" style={{ marginRight: '8px' }} />
                <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                  Donazione Singola
                </Text>
              </Flex>
              <Text fontSize="sm" color="blue.700" lineHeight="1.6">
                Con questa donazione, verrà addebitato un importo di €
                {amount.toFixed(2)} dalla tua carta in un&apos;unica soluzione.
                Il tuo contributo sostiene immediatamente le attività di CIL e
                ti permette di partecipare attivamente alla crescita della
                nostra comunità creativa. Non ci sono costi ricorrenti o
                abbonamenti automatici.
              </Text>
            </Box>

            <Box bg="gray.50" p={4} borderRadius="md">
              <Text fontSize="sm" color="gray.600">
                Importo:
              </Text>
              <Text fontSize="2xl" fontWeight="bold">
                €{amount.toFixed(2)}
              </Text>
            </Box>

            {loading && (
              <Flex justify="center" py={8}>
                <Spinner size="lg" />
              </Flex>
            )}

            {!loading && error && (
              <Text color="red.500" textAlign="center">
                {error}
              </Text>
            )}

            {!loading && clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <PaymentForm ambito="donazione" onSuccess={handleSuccess} />
              </Elements>
            )}
          </VStack>
        </DrawerBody>
        <Divider />
        <DrawerFooter>
          <Button variant="outline" w="full" onClick={onClose}>
            Annulla
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default DonationDrawer;
